'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NotificationDto, NotificationType } from '@vuganawe/types';
import {
  getNotificationPreferences,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreferences,
} from '@/lib/api/public';
import { getLearnerToken } from '@/lib/learnerSession';

const POLL_MS = 60_000;

const TYPE_ICON: Record<NotificationType, string> = {
  reminder: '⏰',
  streak: '🔥',
  certificate: '🏆',
  challenge: '🎯',
  achievement: '⭐',
  system: '🔔',
};

function iconFor(type: NotificationType): string {
  return TYPE_ICON[type] ?? '🔔';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Notification bell for the learner dashboard: unread badge, dropdown list, and
 * mark-as-read on click / mark-all. Polls the API every minute so new reminders
 * and alerts surface without a reload.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [unread, setUnread] = useState(0);
  const [emailReminders, setEmailReminders] = useState<boolean | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const token = getLearnerToken();
    if (!token) return;
    try {
      const res = await getNotifications(token);
      setItems(res.items);
      setUnread(res.unreadCount);
    } catch {
      /* keep the last known state on transient errors */
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function toggleOpen() {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      await load();
      if (emailReminders === null) {
        const token = getLearnerToken();
        if (token) {
          try {
            const prefs = await getNotificationPreferences(token);
            setEmailReminders(prefs.emailReminders);
          } catch {
            /* ignore */
          }
        }
      }
    }
  }

  async function toggleEmailReminders() {
    const token = getLearnerToken();
    if (!token || emailReminders === null) return;
    const next = !emailReminders;
    setEmailReminders(next); // optimistic
    try {
      const prefs = await updateNotificationPreferences(token, next);
      setEmailReminders(prefs.emailReminders);
    } catch {
      setEmailReminders(!next); // revert on failure
    }
  }

  async function handleItemClick(notification: NotificationDto) {
    if (notification.read) return;
    const token = getLearnerToken();
    if (!token) return;
    try {
      const res = await markNotificationRead(token, notification.id);
      setItems(res.items);
      setUnread(res.unreadCount);
    } catch {
      /* ignore */
    }
  }

  async function handleMarkAll() {
    const token = getLearnerToken();
    if (!token) return;
    try {
      const res = await markAllNotificationsRead(token);
      setItems(res.items);
      setUnread(res.unreadCount);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="vn-notif" ref={wrapRef}>
      <button
        type="button"
        className="vn-notif__btn"
        onClick={toggleOpen}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <BellIcon />
        {unread > 0 ? (
          <span className="vn-notif__badge" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="vn-notif__panel" role="dialog" aria-label="Notifications">
          <div className="vn-notif__panel-head">
            <span>Notifications</span>
            {unread > 0 ? (
              <button type="button" className="vn-notif__markall" onClick={handleMarkAll}>
                Mark all read
              </button>
            ) : null}
          </div>

          <div className="vn-notif__list">
            {items.length === 0 ? (
              <p className="vn-notif__empty">You’re all caught up. 🎉</p>
            ) : (
              items.map((notification) => {
                const inner = (
                  <>
                    <span className="vn-notif__icon" aria-hidden="true">
                      {iconFor(notification.type)}
                    </span>
                    <span className="vn-notif__itembody">
                      <span className="vn-notif__title">{notification.title}</span>
                      <span className="vn-notif__text">{notification.body}</span>
                      <span className="vn-notif__time">{timeAgo(notification.createdAt)}</span>
                    </span>
                    {!notification.read ? <span className="vn-notif__dot" aria-hidden="true" /> : null}
                  </>
                );
                const className = `vn-notif__item${notification.read ? '' : ' is-unread'}`;
                return notification.href ? (
                  <Link
                    key={notification.id}
                    href={notification.href}
                    className={className}
                    onClick={() => {
                      void handleItemClick(notification);
                      setOpen(false);
                    }}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={notification.id}
                    type="button"
                    className={className}
                    onClick={() => handleItemClick(notification)}
                  >
                    {inner}
                  </button>
                );
              })
            )}
          </div>

          <div className="vn-notif__panel-foot">
            <label className="vn-notif__pref">
              <span>Email reminders</span>
              <span className="vn-switch">
                <input
                  type="checkbox"
                  checked={emailReminders ?? false}
                  disabled={emailReminders === null}
                  onChange={toggleEmailReminders}
                  aria-label="Email reminders"
                />
                <span className="vn-switch__track" aria-hidden="true">
                  <span className="vn-switch__thumb" />
                </span>
              </span>
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
